import { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { AnnouncementPopup } from "@/components/announcements/AnnouncementPopup";
import { CookieBanner } from "@/components/cookies/CookieBanner";

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { i18n } = useTranslation();

  // Sync i18n language with URL synchronously during render. The
  // useEffect-based sync in useLanguage only fires client-side, so SSG
  // renders default to i18n.init's `lng: "sv"` and prerendered /en HTML
  // ships with Swedish copy. i18next.changeLanguage is synchronous when
  // resources are bundled (no async loader), and idempotent, so calling
  // it in render is safe in both SSG and client paths.
  const langFromPath = location.pathname.match(/^\/(sv|en|no|da|fi)(\/|$)/)?.[1];
  if (langFromPath && i18n.language !== langFromPath) {
    i18n.changeLanguage(langFromPath);
  }

  // Admin path is now /:lang/admin/...
  const isAdmin = /^\/(sv|en|no|da|fi)\/admin(\/|$)/.test(location.pathname) || location.pathname.startsWith("/admin");

  // Don't show header/footer on admin pages
  if (isAdmin) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-md focus:text-sm focus:font-semibold">
        Hoppa till innehåll
      </a>
      <Header />
      <main id="main-content" className="flex-1 pt-16 lg:pt-20">{children}</main>
      <Footer />
      <AnnouncementPopup />
      <CookieBanner />
    </div>
  );
}
